import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';

import { usePlacementApi } from '@post-office/use-placement-api';

export type ViewInsightOutcome = 'opened' | 'alreadyExpanded' | 'targetMissing';

export type ChangeboardingActions = {
	dismiss: VoidFunction;
	viewInsight: (outcome: ViewInsightOutcome) => void;
};

// Expected props for your message component (see next code snippet)
export type CustomPlacementProps = {
	children: (actions: ChangeboardingActions) => React.ReactNode;
	dismiss: VoidFunction;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'jira-reports-agent-roi-spotlight',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
	},
};

const placement = {
	createFrontend:
		createPlacementFrontend<CustomRenderer<CustomPlacementProps>['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<CustomPlacementProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<CustomPlacementProps>;
	},
	U
> = placement.createFrontend;

export type RenderProps = {
	children: (actions: ChangeboardingActions) => React.ReactNode;
	defaultRender?: React.ReactNode;
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<CustomPlacementProps>;
	},
	RenderProps
> = createFrontend<RenderProps>((Messages) => ({ children, defaultRender = null }) => {
	const { data } = usePlacementApi<{
		messages: Array<EligibleMessage>;
	}>('/');
	const maybeEligibleMessage = data?.messages?.[0];
	// Render the eligible message passed to this placement (if any)
	if (maybeEligibleMessage) {
		return (
			<Messages.render {...maybeEligibleMessage} dismiss={() => {}}>
				{children}
			</Messages.render>
		);
	}

	// Render your default UI (including nothing at all)
	return <>{defaultRender}</>;
});
